import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Button,
  ConfigProvider,
  Empty,
  Grid as GridAntd,
  Input,
  Table,
  Tag,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import {
  ClockCircleOutlined,
  ContainerOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import { useWorkflowContainerThree } from "../../../../../libs/apis";
import { WorkflowContainerThreeI } from "../../../../../libs/interfaces";
import { COLOR_PRIMARY } from "../../../../../libs/constants";
import { PeriodKey, periodLabel, periodRange } from "./period";

// Mismos colores de las tarjetas del dashboard para identificar cada proceso
const PROCESS_COLORS: Record<string, string> = {
  vacio: "#F4623A",
  full: "#F59E0B",
  checkpoint: "#22A745",
  camion: "#1677FF",
  workflow: "#1677FF",
};

const normalize = (value?: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const processColor = (type?: string) =>
  PROCESS_COLORS[normalize(type)] ?? COLOR_PRIMARY;

interface TableComponentPropsI {
  period: PeriodKey;
}

export const TableComponent = React.memo(({ period }: TableComponentPropsI) => {
  const screens = GridAntd.useBreakpoint();
  const isMobile = !screens.md;

  const [sorting, setSorting] = useState([{ id: "id", desc: true }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [search, setSearch] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");

  const range = React.useMemo(() => periodRange(period), [period]);

  // Al cambiar el periodo se vuelve a la primera pagina
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [period]);

  const { workflowContainerThreeQuery } = useWorkflowContainerThree({
    sorting,
    pagination,
    globalFilter,
    range,
    rangeVal: range,
  });
  const { data, isLoading, isRefetching, refetch } =
    workflowContainerThreeQuery;

  const columns: ColumnsType<WorkflowContainerThreeI> = [
    {
      title: "Contenedor",
      dataIndex: "container",
      key: "container",
      sorter: true,
      render: (value: string, record) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 16,
              color: COLOR_PRIMARY,
              bgcolor: `${COLOR_PRIMARY}14`,
            }}
          >
            <ContainerOutlined />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontWeight: 700, fontSize: 14, color: COLOR_PRIMARY }}
            >
              {value}
            </Typography>
            {isMobile && record.userCreation ? (
              <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary" }}>
                {record.userCreation}
              </Typography>
            ) : null}
          </Box>
        </Box>
      ),
    },
    {
      title: "Tipo de proceso",
      dataIndex: "processType",
      key: "processType",
      sorter: true,
      render: (value: string) => {
        const color = processColor(value);
        return (
          <Tag
            bordered={false}
            style={{
              margin: 0,
              fontWeight: 600,
              color,
              background: `${color}1F`,
              borderRadius: 12,
              padding: "1px 12px",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: color,
                marginRight: 6,
              }}
            />
            {value}
          </Tag>
        );
      },
    },
    {
      title: "Hora",
      dataIndex: "hour",
      key: "hour",
      sorter: true,
      align: "right",
      render: (value: string, record) => (
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}
          >
            <ClockCircleOutlined style={{ color: "#8a94a6", marginRight: 6 }} />
            {value}
          </Typography>
          {!isMobile && record.createdAt ? (
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              {String(record.createdAt)}
            </Typography>
          ) : null}
        </Box>
      ),
    },
  ];

  const handleTableChange = (
    pag: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<WorkflowContainerThreeI> | SorterResult<WorkflowContainerThreeI>[]
  ) => {
    setPagination({
      pageIndex: (pag.current ?? 1) - 1,
      pageSize: pag.pageSize ?? 10,
    });

    const sort = Array.isArray(sorter) ? sorter[0] : sorter;
    if (sort?.order && sort.field) {
      setSorting([{ id: String(sort.field), desc: sort.order === "descend" }]);
    } else {
      setSorting([{ id: "id", desc: true }]);
    }
  };

  const handleSearch = (value: string) => {
    setGlobalFilter(value.trim());
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: COLOR_PRIMARY },
        components: {
          Table: {
            headerBg: "#f1f5f9",
            headerColor: COLOR_PRIMARY,
            headerSplitColor: "transparent",
            rowHoverBg: `${COLOR_PRIMARY}0D`,
            borderColor: "#e6ebf2",
          },
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: 2,
          border: "1px solid #dde3ec",
          borderTop: `4px solid ${COLOR_PRIMARY}`,
          boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.5,
            borderBottom: "1px solid #e6ebf2",
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: 16, color: COLOR_PRIMARY }}
            >
              Últimos movimientos
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
              Contenedores registrados · {periodLabel(period)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Input.Search
              allowClear
              placeholder="Buscar contenedor"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) handleSearch("");
              }}
              onSearch={handleSearch}
              enterButton={<SearchOutlined />}
              style={{ width: isMobile ? "100%" : 260 }}
            />
            <Button
              icon={<ReloadOutlined />}
              loading={isRefetching}
              onClick={() => refetch()}
            />
          </Box>
        </Box>
        <Table<WorkflowContainerThreeI>
          rowKey={(record) => `${record.processType}-${record.id}`}
          columns={columns}
          dataSource={data?.data?.dato ?? []}
          loading={isLoading || isRefetching}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          size={isMobile ? "small" : "middle"}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No se encontraron registros"
              />
            ),
          }}
          pagination={{
            current: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            total: data?.data?.total ?? 0,
            simple: isMobile,
            showSizeChanger: !isMobile,
            pageSizeOptions: [10, 20, 50],
            showTotal: isMobile
              ? undefined
              : (total, range) => `${range[0]}-${range[1]} de ${total}`,
            style: { padding: "0 16px" },
          }}
        />
      </Box>
    </ConfigProvider>
  );
});
